import { WorkflowCondition } from "lib/api";
import { formatInTimezone } from "lib/utils/datetime";
import { Query, QueryGroup } from "./types";

const conditionValueToRuleValue = (
  condition: WorkflowCondition,
  timezone: string
) => {
  if (condition.type === "boolean") {
    return condition.value === "true";
  }

  if (condition.type === "numeric" && condition.operator === "range") {
    if (condition.rangevalues != null) {
      return {
        from:
          condition.rangevalues[0] != null
            ? Number(condition.rangevalues[0])
            : "",
        to:
          condition.rangevalues[1] != null
            ? Number(condition.rangevalues[1])
            : "",
      };
    }
  }

  if (condition.type === "date and time" && condition.operator !== "range") {
    if (condition.value != null && typeof condition.value === "string") {
      const convertedDateTime =
        condition.value.indexOf(" ") === -1
          ? formatInTimezone(
              condition.value as string,
              timezone,
              "YYYY-MM-DD HH:mm:ss"
            )
          : condition.value;

      const parts = convertedDateTime.split(" ");
      return {
        date: parts[0] !== "undefined" ? parts[0] : undefined,
        time: parts[1] !== "undefined" ? parts[1] : undefined,
      };
    }
  }

  if (condition.type === "date and time" && condition.operator === "range") {
    if (condition.rangevalues != null) {
      const convertedStartDateTime =
        condition.rangevalues[0].indexOf(" ") === -1
          ? formatInTimezone(
              condition.rangevalues[0],
              timezone,
              "YYYY-MM-DD HH:mm:ss"
            )
          : condition.rangevalues[0];
      const convertedEndDateTime =
        condition.rangevalues[1].indexOf(" ") === -1
          ? formatInTimezone(
              condition.rangevalues[1],
              timezone,
              "YYYY-MM-DD HH:mm:ss"
            )
          : condition.rangevalues[1];

      const startParts = convertedStartDateTime.split(" ") ?? [];
      const endParts = convertedEndDateTime.split(" ") ?? [];
      return {
        start: {
          date: startParts[0] !== "undefined" ? startParts[0] : undefined,
          time: startParts[1] !== "undefined" ? startParts[1] : undefined,
        },
        end: {
          date: endParts[0] !== "undefined" ? endParts[0] : undefined,
          time: endParts[1] !== "undefined" ? endParts[1] : undefined,
        },
      };
    }
  }

  if (condition.type === "distance") {
    if (condition.rangevalues != null) {
      return {
        distance: condition.value as string,
        latitude:
          condition.rangevalues[1] != null &&
          condition.rangevalues[1] !== "" &&
          !Number.isNaN(Number(condition.rangevalues[1]))
            ? Number(condition.rangevalues[1])
            : undefined,
        longitude:
          condition.rangevalues[2] != null &&
          condition.rangevalues[2] !== "" &&
          !Number.isNaN(Number(condition.rangevalues[2]))
            ? Number(condition.rangevalues[2])
            : undefined,
        address:
          condition.rangevalues[3] != null
            ? condition.rangevalues[3]
            : undefined,
      };
    }
  }

  return condition.value as string;
};

export const workflowConditionsToQuery = (
  conditions: Array<WorkflowCondition> | undefined,
  timezone: string
): Query | undefined => {
  const groupMap: Record<number, QueryGroup> = {};
  if (conditions != null) {
    conditions.forEach((condition) => {
      let group = groupMap[condition.groupId];
      if (group == null) {
        group = {
          combinator: condition.groupLogic,
          rules: [],
          id: Math.random(),
        };
        groupMap[condition.groupId] = group;

        if (condition.groupId !== 0) {
          groupMap[0].rules.push(group);
        }
      }

      group.rules.push({
        attribute: condition.property,
        operator: condition.operator,
        value: conditionValueToRuleValue(condition, timezone),
        id: Math.random(),
      });
    });

    return groupMap[0];
  }

  return undefined;
};
